import update from "immutability-helper";

import { uuidgen } from "@js/helpers/uuid.js";

import sha256 from "@js/helpers/sha256.js";
import normalizeNewlines from "@js/helpers/normalize_newlines";

import { Base64 } from 'js-base64';

// Retrieve content data.

export const getContentIndex = (state, clientContentID) => {
  return state.data.contents.findIndex(
    (c) => c.client_content_id === clientContentID,
  );
};

export const getContent = (state, clientContentID) => {
  let i = getContentIndex(state, clientContentID);

  return i >= 0 ? state.data.contents[i] : null;
};

export const getAttachmentIndex = (state, clientAttachmentID) => {
  return state.data.attachments.findIndex(
    (a) => a.client_attachment_id === clientAttachmentID,
  );
};

export const getAttachment = (state, clientAttachmentID) => {
  let i = getAttachmentIndex(state, clientAttachmentID);
  return i >= 0 ? state.data.attachments[i] : null;
};

// Create content

export const addContent = (state, data) => {
  return incrementDataGeneration(
    update(state, {
      data: {
        contents: {
          $push: [
            update(data, {
              $merge: {
                id: null,
                client_content_id: uuidgen(),
                preview_image: {
                  step: "start",
                  error_generation_id: null,
                  image_url: "",
                  preview_hash: null,
                },
                published_sha256: {},
                validation_errors: null,
              },
            }),
          ],
        },
      },
    }),
  );
};

export const removeContent = (state, clientContentID) => {
  let updatedContents = [];

  state.data.contents.forEach((content) => {
    if (content.client_content_id !== clientContentID) {
      updatedContents.push(content);
    }
  });

  return incrementDataGeneration(
    update(state, {
      data: {
        $merge: {
          contents: updatedContents,
        },
      },
    }),
  );
};

// Update content data.

export const mergeContent = (state, clientContentID, data = {}) => {
  return incrementDataGeneration(
    mergeContentWithoutBump(state, clientContentID, data),
  );
};

export const mergeContentWithoutBump = (state, clientContentID, data = {}) => {
  let i = getContentIndex(state, clientContentID);

  if (i < 0) return state;

  let normalizedData = data;

  normalizedData = data.html
    ? update(data, {
        $merge: {
          html: normalizeNewlines(data.html),
        },
      })
    : normalizedData;

  normalizedData = data.text
    ? update(normalizedData, {
        $merge: {
          text: normalizeNewlines(data.text),
        },
      })
    : normalizedData;

  return update(state, {
    // Update the content data.
    data: {
      contents: {
        [i]: {
          $merge: normalizedData,
        },
      },
    },
  });
};

export const mergePreviewImageWithoutBump = (
  state,
  clientContentID,
  data = {},
) => {
  let i = getContentIndex(state, clientContentID);

  if (i < 0) return state;

  let content = getContent(state, clientContentID);
  let contentWithImageData = update(content, {
    preview_image: {
      $merge: data,
    },
  });
  return mergeContentWithoutBump(state, clientContentID, contentWithImageData);
};

export const incrementDataGeneration = (state) => {
  return update(state, {
    sync: {
      $merge: {
        data_generation_id: state.sync.data_generation_id + 1,
        network_failure_count: 0,
        network_failure_stopped: false,
      },
    },
  });
};

export const updateContentPublishedSha256 = (
  state,
  clientContentID,
  field,
  sha256,
) => {
  let i = getContentIndex(state, clientContentID);

  if (i < 0) return state;

  return update(state, {
    data: {
      contents: {
        [i]: {
          published_sha256: {
            $merge: { [field]: sha256 },
          },
        },
      },
    },
  });
};

// Update sync state.

export const mergeSyncState = (state, data) => {
  return update(state, {
    sync: {
      $merge: data,
    },
  });
};

// Manage modals.

export const newModal = (state, modalName, initialModalState = {}) => {
  return update(state, {
    modal: {
      $set: {
        name: modalName,
        uuid: uuidgen(),
        state: initialModalState,
      },
    },
  });
};

export const mergeModalState = (state, modalState) => {
  return update(state, {
    modal: {
      state: {
        $merge: modalState,
      },
    },
  });
};

export const setModalState = (state, modalState) => {
  return update(state, {
    modal: {
      state: {
        $set: modalState,
      },
    },
  });
};

export const closeModal = (state) => {
  return update(state, {
    modal: { $set: null },
  });
};

// onSameModal is used to prevent a callback from being executed if the modal
// has changed since the action was requested.
export const onSameModal = (getState, fn) => {
  const initialState = getState();
  const initialModal = initialState.modal && initialState.modal.uuid;
  const self = this;

  // We need to use the bare "function" keyword here, so that "arguments" is
  // filled in with the correct arguments. If we used the () => {} syntax here,
  // "arguments" would be getState and fn from above.
  return function() {
    const finalState = getState();
    const finalModal = finalState.modal && finalState.modal.uuid;

    if ( initialModal != finalModal ) return;

    return fn.apply(self, arguments);
  };
}

// Manage screens.

export const newScreen = (state, screenName, initialScreenState = {}) => {
  return update(state, {
    screen: {
      $set: {
        name: screenName,
        uuid: uuidgen(),
        state: initialScreenState,
      },
    },
  });
};

export const mergeScreenState = (state, screenState) => {
  return update(state, {
    screen: {
      state: {
        $merge: screenState,
      },
    },
  });
};

export const setScreenState = (state, screenState) => {
  return update(state, {
    screen: {
      state: {
        $set: screenState,
      },
    },
  });
};

export const closeScreen = (state) => {
  return update(state, {
    screen: { $set: null },
  });
};

// Attachments.

export const addAttachment = (state, attachment) => {
  if (
    !attachment ||
    !attachment.size ||
    !attachment.data_base64 ||
    !attachment.attachment_name
  ) {
    return state;
  }

  const encodedData = attachment.data_base64;
  const rawData = Base64.atob(encodedData);

  return update(incrementDataGeneration(state), {
    data: {
      attachments: {
        $push: [
          {
            id: null,
            client_attachment_id: uuidgen(),
            attachment_name: attachment.attachment_name,
            data_base64: encodedData,
            size: attachment.size,
            sha256: sha256(rawData, "binary"),
          },
        ],
      },
    },
  });
};

export const removeAttachment = (state, attachmentUUID) => {
  if (!attachmentUUID) return state;

  let filteredAttachments = state.data.attachments.filter((attachment) => {
    return attachment.client_attachment_id !== attachmentUUID;
  });

  return update(incrementDataGeneration(state), {
    data: {
      attachments: {
        $set: filteredAttachments,
      },
    },
  });
};

export const mergeAttachmentWithoutBump = (state, clientAttachmentID, data) => {
  if (!clientAttachmentID) return state;

  let i = getAttachmentIndex(state, clientAttachmentID);

  if (i < 0) return state;

  return update(state, {
    // Update the attachment data.
    data: {
      attachments: {
        [i]: {
          $merge: data,
        },
      },
    },
  });
};

export const getPreviewHashForContent = (state, clientContentID) => {
  let content = getContent(state, clientContentID);
  let previewType = content.format === "text" ? "text" : "html";
  let sha = sha256(content[previewType]);
  return `${previewType}:${sha}`;
};

// Validate modal state.

// state = the current state of the application
//
// validators =
//   object mapping fields to arrays of validator functions
//   these functions accept one argument (The current modal state) and returns an array of errors.
// e.g.:
//   validateModalState(state, {
//     subject: [
//       (s) => s.subject.length < 1 ? [ "Subject cannot be blank" ] : null,
//     ],
//   });
//
// this function returns the application state, with the modal state updated for any errors.
export const validateModalState = (state, validators = []) => {
  if (!state.modal) return state;

  let modalState = state.modal.state;

  // Iterate through the list of fields we are validating.
  for (let field in validators) {
    let fieldValidators = validators[field];

    // Iterate through the list of validators for that field.
    for (let validator of fieldValidators) {
      let errors = validator(modalState);

      // Append the errors to the validation errors for that field.

      if (errors && errors.length > 0) {
        if (!modalState.validation_errors) {
          modalState = update(modalState, { validation_errors: { $set: {} } });
        }

        if (!modalState.validation_errors[field]) {
          modalState = update(modalState, {
            validation_errors: { [field]: { $set: [] } },
          });
        }

        modalState = update(modalState, {
          validation_errors: { [field]: { $push: errors } },
        });
      }
    }
  }

  return setModalState(state, modalState);
};
